import React, { useState, useRef, useCallback, useEffect } from "react";
import Source from "./Source";
import Target from "./Target";
import Operation from "./Operation";
import styled from "styled-components/macro";
import { MdAdd } from "react-icons/md";
import {
  BusinessRuleSourceValues,
  BusinessRuleTargetValues,
  BusinessRuleVariables,
  BusinessRuleVariableOperations,
  ColumnModifiers,
} from "./buildingBlocks";
import Attachments from "./Attachments";
import { patterns } from "./patterns";
import ReactTooltip from "react-tooltip";
import { MdInfoOutline } from "react-icons/md";
import RenderOperation from "../../../components/RuleFragment/OperationHelper";
import DOMPurify from "dompurify";
import { Scrollbars } from "react-custom-scrollbars";
import MarkdownFetcher from "../../../views/FeedsPage/transformerFunctions/MarkDownFetcher";
import Modal from "../../Modal";
import { MdOutlineHelp } from "react-icons/md";

const DetailsToggle = styled.div`
  transition: 0.3s all;
  display: inline-block;
  height: 16px;
  width: 16px;
  color: grey;
  transform: ${(props) => (props.showAttachments ? "rotate(45deg)" : "")};
`;

const DetailsToggleWrapper = styled.div`
  padding: 0.5rem;
  background: ${(props) => props.theme.secondarySurface};
  display: flex;
  margin-right: ${(props) => (props.showAttachments ? "1rem" : "")};
  margin-left: 1rem;
  &:hover {
    ${DetailsToggle} {
      transform: ${(props) => (props.showAttachments ? "" : "rotate(90deg)")};
      cursor: pointer;
      color: black;
    }
  }
`;

const FragmentInner = styled.div`
  padding: 1rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;

const FragmentElement = styled.div`
  margin-right: 1rem;
  display: flex;
  align-items: center;
`;

const FragmentContainer = styled.div`
  transition: max-height 300ms ease-in-out;
  border: 1px solid ${(props) => props.theme.surfaceAlt};
  border-left: 4px solid
    ${(props) => (props.hasError ? "red" : props.theme.onSecondarySurface)};
  cursor: ${(props) => (props.disableAction ? "default" : "pointer")};
`;

function formatAndHighlightNCalc(expression) {
  let indentation = 0;

  // captures all types of replacements: parameters, numbers, operators, functions, and parentheses.
  const pattern =
    /\[([a-zA-Z_]\w*)\]|\b(\d+)\b|(\+\+|\-\-|\*\*|\/\/|\^\^|\%\%|==|!=|>=|<=|&&|\|\|)|\b([a-zA-Z_]\w*)\(|([\(\)])/g;

  //  check which capture group matched and decide the replacement based on it
  expression = expression.replace(pattern, (match, p1, p2, p3, p4, p5) => {
    if (p1) return '<span class="ncalc-parameter">[' + p1 + "]</span>";
    if (p2) return '<span class="ncalc-number">' + p2 + "</span>";
    if (p3) return '<span class="ncalc-operator">' + p3 + "</span>";
    if (p4) return '<span class="ncalc-function">' + p4 + "</span>(";
    if (p5) return '<span class="ncalc-parenthesis">' + p5 + "</span>";
    return match; // Default return
  });

  // Format the expression
  expression = expression.replace(
    /(<span class="ncalc-operator">(&&|\|\|)<\/span>)([ \t]*)(<span class="ncalc-parameter">\[\w+\]<\/span>|<span class="ncalc-number">\d+<\/span>|\w+)/g,
    (match, p1, p2, spaces, p4) => {
      const indent = " ".repeat(indentation * 2);
      return p1 + "\n" + indent + p4; // Put the operator at the end and start the next condition/parameter on the next line
    }
  );

  return expression.trim(); // Trim any leading or trailing spaces
}

const AttachmentToggleComponent = ({
  toggleShowAttachments,
  showAttachments,
}) => {
  function handleClicks(e) {
    e.stopPropagation();
    toggleShowAttachments(!showAttachments);
  }
  return (
    <DetailsToggleWrapper
      showAttachments={showAttachments}
      onClick={(e) => handleClicks(e)}
      data-testid={`fragment-toggle-tool`}
    >
      <DetailsToggle showAttachments={showAttachments}>
        <MdAdd />
      </DetailsToggle>
    </DetailsToggleWrapper>
  );
};
const FragmentComponent = (props) => {
  return (
    <FragmentContainer disableAction={props?.disableAction}>
      <Fragment {...props} />
    </FragmentContainer>
  );
};

export default FragmentComponent;

const Fragment = ({
  element,
  dispatch,
  index,
  editView,
  isEditing,
  isFilter,
  enableTest,
  testRow,
  goodTestParse,
  disableAction,
}) => {
  //Show Helper
  const [showHelper, setShowHelper] = useState(false);

  const setHelper = (helperName) => {
    setShowHelper(helperName);
  };

  const [details, toggleDetails] = useState(false);
  const [showAttachments, toggleShowAttachments] = useState(false);

  const node = useRef(null);

  const handleClick = useCallback(
    (e) => {
      // we have to check to make sure the click is within the app and not a modal
      const container = document.getElementById("root");

      e.stopPropagation();
      //if the click is within the root we can conclude that the click was not in a modal portal
      if (container.contains(e.target)) {
        // check to make sure the click within root was also out of the target
        if (!node.current.contains(e.target)) {
          // click was detected inside the node, handle it here
          // click outside of node detected, close menu
          toggleDetails(false);
        }
      }
    },
    [toggleDetails]
  );

  useEffect(() => {
    // add listener on mount
    document.addEventListener("mousedown", handleClick);
    // when unmounted this return will be called
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [handleClick]);

  const sourceDetails = element?.source;
  const operationDetails = element?.operation;
  const targetDetails = element?.target;

  const businessOperation =
    element?.operation?.typeInformation?.[1]?.typeValue?.value;

  const isReconciliationRule =
    businessOperation === 14 || businessOperation === 15;

  const isByAmount = element?.operation?.typeInformation[2]?.typeValue === 4;
  const isByPercentage =
    element?.operation?.typeInformation[2]?.typeValue === 5;
  const isByExactAmount =
    element?.operation?.typeInformation[2]?.typeValue === 7;
  const isByExactPercentage =
    element?.operation?.typeInformation[2]?.typeValue === 8;
  const isByAmountNonAbsolute =
    element.operation.typeInformation[2]?.typeValue === 9;
  const isByPercentageNonAbsolute =
    element.operation.typeInformation[2]?.typeValue === 10;
  const isByExactAmountNonAbsolute =
    element.operation.typeInformation[2]?.typeValue === 11;
  const isByExactPercentageNonAbsolute =
    element.operation.typeInformation[2]?.typeValue === 12;

  // currently checking for value on source side, if so we hide it from user
  const isNotCalcValue =
    sourceDetails?.typeInformation[1]?.typeValue?.value !== 4;

  if (details)
    return (
      <FragmentInner ref={node}>
        {isNotCalcValue && (
          <>
            <Source
              node={element}
              dispatch={dispatch}
              index={index}
              enableTest={enableTest}
              testRow={testRow}
              goodTestParse={goodTestParse}
              isFilter={isFilter}
            />
            <Operation
              node={element}
              dispatch={dispatch}
              index={index}
              isReconciliationRule={isReconciliationRule}
              isEditing={isEditing}
            />
          </>
        )}

        {element?.target ? (
          <Target
            node={element}
            dispatch={dispatch}
            index={index}
            isFilter={isFilter}
            enableTest={enableTest}
            testRow={testRow}
            goodTestParse={goodTestParse}
          />
        ) : null}
        {showAttachments && (
          <Attachments
            index={index}
            dispatch={dispatch}
            showAttachments={showAttachments}
            toggleShowAttachments={toggleShowAttachments}
            type={"Fragment"}
          />
        )}

        {editView && (
          <AttachmentToggleComponent
            index={index}
            showAttachments={showAttachments}
            toggleShowAttachments={toggleShowAttachments}
          />
        )}
      </FragmentInner>
    );

  const isSum =
    sourceDetails?.typeInformation[2]?.typeValue?.value === 4 ||
    sourceDetails?.typeInformation[2]?.typeValue?.value === 3;

  // Parse fragmentValue only if isSum is true
  function parseToNestedArray(input) {
    // Step 1: Add double quotes around words or phrases that need to be strings
    const jsonCompatibleInput = input
      .replace(/\[/g, '["')
      .replace(/\]/g, '"]')
      .replace(/,(\s*)\[/g, ",[") // clean up extra quotes around brackets
      .replace(/"\[/g, "[") // remove any opening quotes before brackets
      .replace(/\]"/g, "]") // remove any closing quotes after brackets
      .replace(/"\s*,\s*"/g, '","'); // retain commas between quoted strings

    // Step 2: Parse the adjusted string to create a JavaScript array
    try {
      let parsedArray = JSON.parse(jsonCompatibleInput);

      // Step 3: Ensure the structure has at least two arrays for Group_By and Sum_By
      if (!Array.isArray(parsedArray)) parsedArray = [[], []];
      if (!Array.isArray(parsedArray[0])) parsedArray[0] = [];
      if (!Array.isArray(parsedArray[1])) parsedArray[1] = [];

      // Step 4: Filter out empty strings or invalid entries within Group_By and Sum_By
      parsedArray[0] = parsedArray[0].filter(
        (item) => Array.isArray(item) && item[0].trim() !== ""
      );
      parsedArray[1] = parsedArray[1].filter(
        (item) => Array.isArray(item) && item[0].trim() !== ""
      );

      return parsedArray;
    } catch (error) {
      console.error("Parsing failed:", error.message);
      return [[], []]; // Default to two empty arrays if parsing fails
    }
  }

  // Conditionally parse and format fragmentValue only if isSum is true
  const formattedFragmentValue = isSum
    ? parseToNestedArray(sourceDetails?.typeInformation[1]?.fragmentValue)
    : null;

  return (
    <FragmentInner
      ref={node}
      onClick={() => (disableAction ? null : toggleDetails(!details))}
      data-testid="testFragmentWrapper"
    >
      {isNotCalcValue ? (
        <>
          <FragmentElement>
            <>
              {sourceDetails?.typeInformation[1]?.fragmentValue ? (
                isSum &&
                formattedFragmentValue &&
                Array.isArray(formattedFragmentValue) ? (
                  <>
                    <div>
                      <div
                        style={{
                          padding: ".5rem",
                          backgroundColor: "#efefef",
                          marginRight: ".5rem",
                          marginBottom: ".5rem",
                        }}
                      >
                        <strong>Group By Columns:</strong>
                        {formattedFragmentValue[0]?.map((group, i) => (
                          <div key={`group-${i}`} style={{ margin: "4px 0" }}>
                            {group[0]}
                          </div>
                        ))}
                      </div>
                      {sourceDetails?.typeInformation[2]?.typeValue?.value ===
                        4 && (
                        <div
                          style={{
                            padding: ".5rem",
                            backgroundColor: "#efefef",
                          }}
                        >
                          <strong>Sum By Column:</strong>
                          {formattedFragmentValue[1]?.map((sum, i) => (
                            <div key={`sum-${i}`} style={{ margin: "4px 0" }}>
                              {sum[0]}
                            </div>
                          ))}
                        </div>
                      )}

                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            fontSize: ".8rem",
                            alignItems: "center",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setHelper("MODIFIERS");
                          }}
                        >
                          Modifiers Help
                          <MdOutlineHelp
                            style={{
                              marginLeft: ".5rem",
                              fontSize: "1.2rem",
                              cursor: "pointer",
                            }}
                            title="Modifiers Help"
                          />
                        </div>
                      </div>
                    </div>

                    {showHelper ? (
                      <Modal hide={() => setShowHelper(false)}>
                        <Scrollbars
                          autoHeightMax={window.innerHeight * 0.7}
                          autoHeight
                        >
                          <div
                            style={{
                              padding: "1rem",
                              background: "#fbfbfb",
                              marginBottom: "1rem",
                            }}
                          >
                            <MarkdownFetcher
                              hideClose
                              MdFile={showHelper}
                              close={setShowHelper}
                            />
                          </div>
                        </Scrollbars>
                      </Modal>
                    ) : null}
                  </>
                ) : (
                  // Fallback for non-sum or non-grouped fragmentValue
                  <span>
                    {sourceDetails?.typeInformation[1]?.fragmentValue}
                  </span>
                )
              ) : (
                BusinessRuleSourceValues[
                  sourceDetails?.typeInformation[1]?.typeValue?.value - 1
                ]?.label
              )}
              {sourceDetails?.typeInformation[2]?.typeValue
                ? `(${
                    ColumnModifiers[
                      sourceDetails?.typeInformation[2]?.typeValue?.value - 1
                    ]?.label
                  }) `
                : null}
              {sourceDetails?.typeInformation[2]?.typeValue?.value === 2
                ? `(Days: ${sourceDetails?.typeInformation[2]?.fragmentValue}) `
                : null}
            </>
          </FragmentElement>
          <FragmentElement>
            <RenderOperation operationDetails={operationDetails} />
          </FragmentElement>
        </>
      ) : null}

      {targetDetails !== null || targetDetails !== undefined ? (
        <>
          {element?.target && (
            <FragmentElement>
              <div>
                {targetDetails?.typeInformation[1]?.fragmentValue === null
                  ? BusinessRuleTargetValues[
                      targetDetails?.typeInformation[1]?.typeValue?.value - 1
                    ]?.label
                  : null}
              </div>

              {targetDetails?.typeInformation[1]?.typeValue?.value === 3 ? (
                <div>
                  {
                    BusinessRuleVariables[
                      targetDetails?.typeInformation[1]?.fragmentValue?.value -
                        1
                    ]?.label
                  }
                </div>
              ) : businessOperation === 13 ? (
                <>
                  {patterns.find(
                    (p) =>
                      p?.value ===
                      targetDetails?.typeInformation[1]?.fragmentValue
                  )?.label ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {
                        patterns.find(
                          (p) =>
                            p?.value ===
                            targetDetails?.typeInformation[1]?.fragmentValue
                        )?.label
                      }
                      <div
                        data-tip={
                          patterns.find(
                            (p) =>
                              p?.value ===
                              targetDetails?.typeInformation[1]?.fragmentValue
                          )?.description
                        }
                        data-for="patternHelper"
                      >
                        <MdInfoOutline />
                      </div>
                      <ReactTooltip id="patternHelper" type="info">
                        <span>
                          {
                            patterns.find(
                              (p) =>
                                p?.value ===
                                targetDetails?.typeInformation[1]?.fragmentValue
                            )?.description
                          }
                        </span>
                      </ReactTooltip>
                    </div>
                  ) : (
                    <div>
                      {targetDetails?.typeInformation[1]?.fragmentValue}
                    </div>
                  )}
                </>
              ) : (
                <div>
                  {isByAmount ||
                  isByPercentage ||
                  isByExactAmount ||
                  isByExactPercentage ||
                  isByAmountNonAbsolute ||
                  isByPercentageNonAbsolute ||
                  isByExactAmountNonAbsolute ||
                  isByExactPercentageNonAbsolute
                    ? "By"
                    : null}
                  {isByExactAmount ||
                  isByExactPercentage ||
                  isByExactAmountNonAbsolute ||
                  isByExactPercentageNonAbsolute
                    ? " Exactly"
                    : null}{" "}
                  {isNotCalcValue
                    ? targetDetails?.typeInformation[1]?.fragmentValue
                    : targetDetails?.typeInformation[1]?.fragmentValue && (
                        <pre
                          id="ncalc-expression"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              formatAndHighlightNCalc(
                                targetDetails?.typeInformation[1]?.fragmentValue
                              )
                            ),
                          }}
                        />
                      )}
                  {isByPercentage ||
                  isByExactPercentage ||
                  isByExactPercentageNonAbsolute
                    ? "%"
                    : null}
                </div>
              )}
              {targetDetails?.typeInformation[1]?.typeValue?.value === 3 ? (
                <>
                  {targetDetails?.typeInformation[2]?.typeValue?.value ? (
                    <div style={{ marginLeft: ".5rem", color: "orange" }}>
                      {
                        //not negative because 0 is true for this array
                        BusinessRuleVariableOperations[
                          targetDetails?.typeInformation[2]?.typeValue?.value
                        ]?.label
                      }{" "}
                    </div>
                  ) : null}

                  {targetDetails?.typeInformation[2]?.typeValue?.value !== 0 ? (
                    <div style={{ color: "orange" }}>
                      {targetDetails?.typeInformation[2]?.fragmentValue}
                    </div>
                  ) : null}
                </>
              ) : null}

              {targetDetails?.typeInformation[3]?.typeValue
                ? `(${
                    ColumnModifiers[
                      targetDetails?.typeInformation[3]?.typeValue?.value - 1
                    ]?.label
                  }) `
                : null}

              {targetDetails.typeInformation[3]?.typeValue?.value === 2
                ? `(Days: ${targetDetails?.typeInformation[3]?.fragmentValue}) `
                : null}
            </FragmentElement>
          )}
        </>
      ) : null}

      {editView && (
        <AttachmentToggleComponent
          showAttachments={showAttachments}
          toggleShowAttachments={toggleShowAttachments}
        />
      )}
      {showAttachments && (
        <Attachments
          index={index}
          dispatch={dispatch}
          showAttachments={showAttachments}
          toggleShowAttachments={toggleShowAttachments}
          type={"Fragment"}
        />
      )}
    </FragmentInner>
  );
};
